<template>
  <v-data-table
    dense
    must-sort
    :headers="this.headers"
    :options.sync="options"
    :footer-props="footerProps"
    :server-items-length="this.list.count"
    :items="this.list.items"
    :loading="$apollo.queries.list.loading"
    item-key="_id"
  >
    <template v-slot:[`item.email`]="{ value }">
      <td><a :href="`mailto:${value}`">{{ value }}</a></td>
    </template>
    <template v-slot:[`item.phoneNumber`]="{ value }">
      <td><a :href="`tel:${value}`">{{ value }}</a></td>
    </template>
    <template v-slot:[`item.hasIdentity`]="{ value }">
      <td>{{ value | yesNo }}</td>
    </template>
    <template v-slot:[`item.lastLoggedInAt`]="{ value }">
      <td>{{ value | formatDate }}</td>
    </template>
    <template v-slot:[`item.isActive`]="{ value }">
      <td><v-icon>{{ value | checkMark }}</v-icon></td>
    </template>
    <template v-slot:[`item.registrations`]="{ value }">
      <td>
        <p v-for="rp in value" :key="rp._id">
          <template v-if="rp.isActive">
            {{ getMemberLabel(rp.registration) }}
            <template v-if="rp.isAdminForRegistration">
              &star;
            </template>
            <br/>
          </template>
        </p>
      </td>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <td class="text-end">
        <v-btn
          v-show="item.matchedPersons.length > 0"
          small
          icon
          @click="$emit('open:duplicates', item)"
        >
          <v-icon color="primary">merge</v-icon>
        </v-btn>
        <v-btn small icon @click="$emit('open', item)">
          <v-icon color="primary">edit</v-icon>
        </v-btn>
      </td>
    </template>

    <template v-slot:footer.prepend>
      <v-btn
        icon
        :loading="$apollo.queries.list.loading"
        :disabled="$apollo.queries.list.loading"
        @click="$apollo.queries.list.refresh()"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
    </template>
  </v-data-table>
</template>

<script>
import gql from 'graphql-tag'
import { capitalCase } from 'change-case'

import gqlSortMixin from '../mixins/gql-sort-mixin'

export default {
  name: 'PersonList',

  mixins: [
    gqlSortMixin
  ],

  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    headers: [
      {
        text: 'MSA Person ID',
        value: 'personId'
      },
      {
        text: 'Name',
        value: 'name'
      },
      {
        text: 'Family Name',
        value: 'familyName'
      },
      {
        text: 'Given Name',
        value: 'givenName'
      },
      {
        text: 'Email',
        value: 'email'
      },
      {
        text: 'Phone Number',
        value: 'phoneNumber'
      },
      {
        text: 'Has Login',
        value: 'hasIdentity'
      },
      {
        text: 'Last Log In',
        value: 'lastLoggedInAt'
      },
      {
        text: 'Members',
        value: 'registrations',
        sortable: false
      },
      {
        text: 'Detected Duplicates',
        value: 'matchedPersons.length',
        sortable: false
      },
      {
        text: 'Active',
        value: 'isActive',
        sortable: false
      },
      {
        text: 'Actions',
        value: 'actions',
        class: 'text-end',
        sortable: false
      }
    ],
    options: {
      multiSort: false,
      mustSort: true,
      sortDesc: [true],
      sortBy: ['lastLoggedInAt'],
      page: 1,
      itemsPerPage: 25
    },
    footerProps: {
      'items-per-page-options': [10, 25, 50, 100, 200, 500]
    },
    list: {
      count: 0,
      items: []
    }
  }),

  apollo: {
    list: {
      query: gql`query PersonListSearchPersons ($filters: FilterFindManyPersonInput, $page: Int!, $perPage: Int!, $sort: SortFindManyPersonInput!) {
        list: personManyPagination(filter: $filters, page: $page, perPage: $perPage, sort: $sort) {
          count
          items {
            _id
            personId
            isActive
            name
            givenName
            familyName
            email
            phoneNumber
            hasIdentity
            lastLoggedInAt
            matchedPersons {
              _id
            }
            registrations {
              _id
              isActive
              isPrimaryContact
              isAdminForRegistration
              registration {
                _id
                memberNumber
                memberName
                abn
                primaryActivityId
                activities {
                  _id
                  type
                  licenseStatus
                }
              }
            }
          }
        }
      }`,
      variables () {
        return {
          filters: this.filters,
          page: this.options.page,
          perPage: this.options.itemsPerPage,
          sort: this.gqlSort(this.options)
        }
      },
      error (e) { this.onError(e) },
      debounce: 250
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    getMemberLabel (registration) {
      const memberNumber = registration.memberNumber || 'Non-Member'

      if (registration.activities.length === 0) {
        return `${memberNumber} (No License)`
      }

      const primaryActivtity = registration.primaryActivityId
        ? registration.activities.find(o => o._id === registration.primaryActivityId)
        : registration.activities[0]

      return `${memberNumber}
        - ${registration.abn || 'No ABN'}
        - ${registration.memberName} (${capitalCase(primaryActivtity.type)}
        - ${capitalCase(primaryActivtity.licenseStatus || 'No License')})`
    }
  }
}
</script>
